/**
 * Deployment Pipeline Page
 * AIDLC Administrative Backend
 *
 * Full-featured deployment pipeline with two tabs:
 *   1. Pipeline — build/deploy stages, log viewer, file diff, history
 *   2. Azure Dashboard — real-time Azure KPI metrics
 *
 * Features:
 *   - Guided Mode toggle for non-technical users (default: ON)
 *   - Real-time log streaming via Socket.io
 *   - Pipeline stage visualization with pulse animations
 *   - Deploy confirmation modal with pre-deploy checklist
 *   - Azure KPI auto-refresh every 60s
 *   - Mock data fallback when backend is unreachable
 *
 * Personas: ops
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Badge } from '../shared/Badge';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { useDeployment } from '../../hooks/useDeployment';
import * as deploymentService from '../../services/deploymentService';
import type {
  DeploymentStatus,
  DeploymentRecord,
  LogEntry,
  AzureKpiData,
} from '../../types/deployment';
import {
  evaluateAllKpis,
  countIssues,
  generateToddPrompt,
  type TroubleshootInfo,
  type DashboardIssues,
} from './azureKnowledgeBase';
import apiClient from '../../services/api';

// ── Constants ─────────────────────────────────────────────────────

const AZURE_REFRESH_INTERVAL = 60_000;

interface PipelineStage {
  key: string;
  label: string;
  description: string;
  tooltip: string;
  statusKeys: DeploymentStatus[];
  icon: React.ReactNode;
}

const PIPELINE_STAGES: PipelineStage[] = [
  {
    key: 'compile_ui',
    label: 'Compile UI',
    description: 'Build the screens and buttons users see',
    tooltip:
      'Converts the TypeScript and React source code into optimized JavaScript and CSS files that web browsers can understand.',
    statusKeys: ['building_frontend'],
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
      </svg>
    ),
  },
  {
    key: 'compile_server',
    label: 'Compile Server',
    description: 'Build the behind-the-scenes logic',
    tooltip:
      'Builds the server-side code that processes data, handles logins, manages security, and communicates with the database.',
    statusKeys: ['building_backend'],
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path d="M5.25 14.25h13.5m-13.5 0a3 3 0 01-3-3m3 3a3 3 0 100 6h13.5a3 3 0 100-6m-16.5-3a3 3 0 013-3h13.5a3 3 0 013 3m-19.5 0a4.5 4.5 0 01.9-2.7L5.737 5.1a3.375 3.375 0 012.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 01.9 2.7m0 0a3 3 0 01-3 3m0 3h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008zm-3 6h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008z" />
      </svg>
    ),
  },
  {
    key: 'package',
    label: 'Package',
    description: 'Zip everything into one file',
    tooltip:
      'Bundles everything into a single zip file that Azure can install -- like packing a suitcase for a trip.',
    statusKeys: ['packaging'],
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
      </svg>
    ),
  },
  {
    key: 'upload',
    label: 'Upload to Azure',
    description: 'Send package to cloud server',
    tooltip:
      'Sends the zip package to Microsoft Azure cloud and installs it on the live server at ldaja.ai.',
    statusKeys: ['deploying'],
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z" />
      </svg>
    ),
  },
  {
    key: 'verify',
    label: 'Verify Live',
    description: 'Confirm the site is healthy',
    tooltip:
      'Checks that ldaja.ai responds correctly and all systems are healthy after the update.',
    statusKeys: ['health_checking'],
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
      </svg>
    ),
  },
];

/** Which stages are considered "complete" for a given status */
function getStageState(
  stageIndex: number,
  currentStatus: DeploymentStatus,
): 'waiting' | 'active' | 'complete' | 'failed' {
  const statusOrder: DeploymentStatus[] = [
    'building_frontend',
    'building_backend',
    'packaging',
    'deploying',
    'health_checking',
  ];
  const failedStatuses: DeploymentStatus[] = [
    'build_failed',
    'package_failed',
    'deploy_failed',
    'health_check_failed',
  ];
  const failedStageMap: Record<string, number> = {
    build_failed: 0, // Could fail at frontend or backend; simplify to stage 0
    package_failed: 2,
    deploy_failed: 3,
    health_check_failed: 4,
  };

  if (currentStatus === 'idle') return 'waiting';

  if (currentStatus === 'deployed' || currentStatus === 'built') {
    // All build stages complete for 'built', all stages for 'deployed'
    const maxComplete = currentStatus === 'built' ? 2 : 4;
    return stageIndex <= maxComplete ? 'complete' : 'waiting';
  }

  if (failedStatuses.includes(currentStatus)) {
    const failedAt = failedStageMap[currentStatus] ?? 0;
    if (stageIndex < failedAt) return 'complete';
    if (stageIndex === failedAt) return 'failed';
    return 'waiting';
  }

  const currentStageIndex = statusOrder.indexOf(currentStatus);
  if (currentStageIndex === -1) return 'waiting';

  if (stageIndex < currentStageIndex) return 'complete';
  if (stageIndex === currentStageIndex) return 'active';
  return 'waiting';
}

/** Status badge config */
function getStatusBadgeConfig(status: DeploymentStatus): {
  label: string;
  variant: 'success' | 'danger' | 'info' | 'warning' | 'neutral';
} {
  switch (status) {
    case 'idle':
      return { label: 'Idle', variant: 'neutral' };
    case 'building_frontend':
      return { label: 'Compiling UI', variant: 'info' };
    case 'building_backend':
      return { label: 'Compiling Server', variant: 'info' };
    case 'packaging':
      return { label: 'Packaging', variant: 'info' };
    case 'built':
      return { label: 'Build Complete', variant: 'success' };
    case 'deploying':
      return { label: 'Deploying', variant: 'warning' };
    case 'health_checking':
      return { label: 'Health Checking', variant: 'warning' };
    case 'deployed':
      return { label: 'Deployed', variant: 'success' };
    case 'build_failed':
      return { label: 'Build Failed', variant: 'danger' };
    case 'package_failed':
      return { label: 'Package Failed', variant: 'danger' };
    case 'deploy_failed':
      return { label: 'Deploy Failed', variant: 'danger' };
    case 'health_check_failed':
      return { label: 'Health Check Failed', variant: 'danger' };
    default:
      return { label: String(status), variant: 'neutral' };
  }
}

/** Plain English description of current status */
function getPlainEnglishText(status: DeploymentStatus): string {
  switch (status) {
    case 'idle':
      return 'Click <strong>Start Build</strong> to begin the deployment process. The pipeline will compile your code, package it, and prepare it for upload to the live server.';
    case 'building_frontend':
      return 'The system is currently <strong>compiling the user interface</strong> -- converting React and TypeScript code into optimized files your browser can run. This usually takes about 30-60 seconds.';
    case 'building_backend':
      return 'The UI is done. Now compiling the <strong>server-side code</strong> that handles authentication, data processing, and API requests. Almost there...';
    case 'packaging':
      return 'Both codebases compiled successfully. Now <strong>creating the deployment package</strong> -- a single zip file containing everything needed to run the site.';
    case 'built':
      return 'Build is complete and the package is ready. Click <strong>Deploy to Production</strong> to upload it to Azure and update the live site at ldaja.ai.';
    case 'deploying':
      return 'The package is being <strong>uploaded to Microsoft Azure</strong> and installed on the production server. The site may be briefly unavailable (10-30 seconds) during this process.';
    case 'health_checking':
      return 'Upload complete. Now <strong>verifying the site is healthy</strong> -- sending a test request to confirm everything is working correctly after the update.';
    case 'deployed':
      return 'Deployment is <strong>complete and verified</strong>. The live site at ldaja.ai is running the latest code. All systems are healthy.';
    case 'build_failed':
      return 'The build <strong>failed</strong>. Check the log output below for error details. Common causes: TypeScript errors, missing dependencies, or syntax issues. Fix the problem and try again.';
    case 'package_failed':
      return 'Compilation succeeded but <strong>packaging failed</strong>. This usually means a problem with the zip creation script. Check the log output for details.';
    case 'deploy_failed':
      return 'The package was built but <strong>deployment to Azure failed</strong>. This could be a network issue, Azure quota limit, or authentication problem. The previous version is still running.';
    case 'health_check_failed':
      return 'Deployment uploaded but the <strong>health check failed</strong>. The site may not be responding correctly. Check if the application started properly.';
    default:
      return 'Status unknown.';
  }
}

/** Format seconds into "Xm Ys" */
function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

/** Format relative time */
function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/** Progress bar color by percentage */
function barColor(pct: number): string {
  if (pct >= 80) return 'bg-red-500';
  if (pct >= 60) return 'bg-yellow-500';
  return 'bg-green-500';
}

// ── Mock data for Azure dashboard fallback ────────────────────────

const AZURE_MOCK_DATA: AzureKpiData = {
  serviceHealth: {
    available: true,
    state: 'Running',
    enabled: true,
    defaultHostName: 'app-ldaja-aidlc-web.azurewebsites.net',
    httpsOnly: true,
  },
  healthCheck: { available: true, statusCode: 200, responseTime: 284 },
  sslStatus: {
    available: true,
    issuer: 'GeoTrust',
    validTo: '2026-08-15T00:00:00Z',
    daysUntilExpiry: 183,
    isValid: true,
  },
  dnsStatus: {
    available: true,
    allHealthy: true,
    okCount: 4,
    totalCount: 4,
    nameservers: [
      { nameserver: 'ns1-08.azure-dns.com', status: 'ok' },
      { nameserver: 'ns2-08.azure-dns.net', status: 'ok' },
      { nameserver: 'ns3-08.azure-dns.org', status: 'ok' },
      { nameserver: 'ns4-08.azure-dns.info', status: 'ok' },
    ],
  },
  performanceMetrics: {
    available: true,
    cpu: { current: 12, dataPoints: [] },
    memory: { current: 58, dataPoints: [], workingSetMB: 1015 },
    responseTime: 284,
    http2xx: 1198,
    http4xx: 43,
    http5xx: 6,
    requests: 1247,
  },
  appInsights: { available: true, failedRequests: 23, availabilityPercentage: 99.7 },
  costData: {
    available: true,
    totalCost: 38.42,
    dailyBurnRate: 3.49,
    projectedMonthEnd: 97.72,
    dayOfMonth: 11,
    daysInMonth: 28,
    costByService: {
      'App Service (B1)': 29.8,
      'Application Insights': 4.62,
      'Azure DNS': 2.5,
      'Log Analytics Workspace': 1.2,
      'Bandwidth / Data Transfer': 0.3,
    },
    currency: 'USD',
  },
  recentAlerts: {
    available: true,
    totalCount: 3,
    bySeverity: { Warning: 2, Critical: 1 },
    alerts: [
      { severity: 'Warning', name: 'Memory usage exceeded 70%', triggered: 'Feb 11, 10:36 AM', duration: '4m 12s', status: 'Resolved' },
      { severity: 'Critical', name: 'Health check failed (503)', triggered: 'Feb 11, 10:34 AM', duration: '28s', status: 'Resolved' },
      { severity: 'Info', name: 'Deployment completed successfully', triggered: 'Feb 11, 10:34 AM', duration: '--', status: 'Informational' },
    ],
  },
  resourceList: { available: true, resources: [], totalCount: 5, allHealthy: true },
};

// ── Main Component ────────────────────────────────────────────────

export function DeploymentPipelinePage() {
  // ── Tab + Guided Mode ──
  const [activeTab, setActiveTab] = useState<'pipeline' | 'dashboard'>('pipeline');
  const [guidedMode, setGuidedMode] = useState(true);

  // ── Pipeline State ──
  const { state, logs, isConnected, triggerBuild, triggerDeploy, cancel, clearLogs } =
    useDeployment();
  const [history, setHistory] = useState<DeploymentRecord[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [deployChecks, setDeployChecks] = useState([false, false, false]);
  const [buildError, setBuildError] = useState<string | null>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [showExplainer, setShowExplainer] = useState(true);
  const [showFileDiff, setShowFileDiff] = useState(true);
  const logViewerRef = useRef<HTMLDivElement>(null);

  // ── Azure Dashboard State ──
  const [kpiData, setKpiData] = useState<AzureKpiData | null>(null);
  const [kpiLoading, setKpiLoading] = useState(false);
  const [kpiError, setKpiError] = useState<string | null>(null);
  const [kpiIsLive, setKpiIsLive] = useState(false);
  const [kpiLastUpdated, setKpiLastUpdated] = useState<Date | null>(null);
  const kpiTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Load deployment history ──
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const data = await deploymentService.getDeploymentHistory(20);
      setHistory(data);
    } catch {
      // Silently fail for history
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Reload history when deployment completes
  useEffect(() => {
    if (state.status === 'deployed' || state.status === 'deploy_failed') {
      loadHistory();
    }
  }, [state.status, loadHistory]);

  // ── Auto-scroll log viewer ──
  useEffect(() => {
    if (autoScroll && logViewerRef.current) {
      logViewerRef.current.scrollTop = logViewerRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  // ── Azure KPI fetching ──
  const fetchKpis = useCallback(async () => {
    setKpiLoading(true);
    try {
      const data = await deploymentService.getAzureKpis();
      setKpiData(data);
      setKpiIsLive(true);
      setKpiError(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.warn('[Deployment] Azure KPI fetch failed, using mock data:', message);
      setKpiData(AZURE_MOCK_DATA);
      setKpiIsLive(false);
      setKpiError(
        `Start the backend server at localhost:4000 to see live data. Showing mock data. (${message})`,
      );
    } finally {
      setKpiLoading(false);
      setKpiLastUpdated(new Date());
    }
  }, []);

  // ── Azure tab auto-refresh ──
  useEffect(() => {
    if (activeTab === 'dashboard') {
      if (!kpiData) fetchKpis();
      kpiTimerRef.current = setInterval(fetchKpis, AZURE_REFRESH_INTERVAL);
    } else {
      if (kpiTimerRef.current) {
        clearInterval(kpiTimerRef.current);
        kpiTimerRef.current = null;
      }
    }
    return () => {
      if (kpiTimerRef.current) clearInterval(kpiTimerRef.current);
    };
  }, [activeTab, fetchKpis, kpiData]);

  // ── Pipeline actions ──
  const handleStartBuild = useCallback(async () => {
    setBuildError(null);
    try {
      await triggerBuild();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Build trigger failed';
      setBuildError(message);
    }
  }, [triggerBuild]);

  const handleDeploy = useCallback(async () => {
    setShowDeployModal(false);
    setDeployChecks([false, false, false]);
    try {
      await triggerDeploy();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Deploy trigger failed';
      setBuildError(message);
    }
  }, [triggerDeploy]);

  const handleCancel = useCallback(async () => {
    try {
      await cancel();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Cancel failed';
      setBuildError(message);
    }
  }, [cancel]);

  // ── Derived state ──
  const isBuilding =
    state.status === 'building_frontend' ||
    state.status === 'building_backend' ||
    state.status === 'packaging';
  const isDeploying = state.status === 'deploying' || state.status === 'health_checking';
  const canStartBuild =
    state.status === 'idle' ||
    state.status === 'deployed' ||
    state.status === 'build_failed' ||
    state.status === 'package_failed' ||
    state.status === 'deploy_failed' ||
    state.status === 'health_check_failed';
  const canDeploy = state.status === 'built';
  const canCancel = isBuilding || isDeploying;
  const allChecked = deployChecks.every(Boolean);

  const statusBadge = getStatusBadgeConfig(state.status);

  // ── History stats ──
  const lastSuccess = useMemo(() => {
    return history.find((h) => h.status === 'success');
  }, [history]);

  const todayDeploys = useMemo(() => {
    const today = new Date().toDateString();
    return history.filter((h) => new Date(h.startedAt).toDateString() === today);
  }, [history]);

  const todaySuccessRate = useMemo(() => {
    if (todayDeploys.length === 0) return 0;
    const successes = todayDeploys.filter((h) => h.status === 'success').length;
    return Math.round((successes / todayDeploys.length) * 100);
  }, [todayDeploys]);

  // ── Elapsed time for active stage ──
  const [elapsedMs, setElapsedMs] = useState(0);
  useEffect(() => {
    if (!state.stageStartedAt) {
      setElapsedMs(0);
      return;
    }
    const timer = setInterval(() => {
      setElapsedMs(Date.now() - new Date(state.stageStartedAt!).getTime());
    }, 1000);
    return () => clearInterval(timer);
  }, [state.stageStartedAt]);

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  return (
    <div className="space-y-0">
      {/* ── Header with Tabs ── */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          {/* Tab Buttons */}
          <div className="flex items-end gap-4">
            <button
              onClick={() => setActiveTab('pipeline')}
              className={`relative pb-2 text-[13px] font-semibold transition-colors ${
                activeTab === 'pipeline' ? 'text-blue-700' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                </svg>
                Pipeline
              </span>
              {activeTab === 'pipeline' && (
                <span className="absolute left-0 right-0 bottom-0 h-0.5 bg-blue-600 rounded" />
              )}
            </button>
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`relative pb-2 text-[13px] font-semibold transition-colors ${
                activeTab === 'dashboard' ? 'text-blue-700' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z" />
                </svg>
                Azure Dashboard
              </span>
              {activeTab === 'dashboard' && (
                <span className="absolute left-0 right-0 bottom-0 h-0.5 bg-blue-600 rounded" />
              )}
            </button>
          </div>
          <Badge variant="info" dot>
            Production &mdash; ldaja.ai
          </Badge>
        </div>

        <div className="flex items-center gap-4">
          {/* Guided Mode Toggle */}
          <label
            className="flex items-center gap-2 cursor-pointer select-none"
            title="Toggle detailed explanations and help text"
          >
            <span className="text-xs text-gray-500 font-medium">Guided Mode</span>
            <div className="relative">
              <input
                type="checkbox"
                checked={guidedMode}
                onChange={(e) => setGuidedMode(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-gray-300 peer-checked:bg-blue-600 rounded-full transition-colors" />
              <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4" />
            </div>
          </label>
          <div className="w-px h-6 bg-gray-200" />
          {/* LIVE Badge */}
          {isConnected && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
              </span>
              <span className="text-xs font-semibold text-green-700 tracking-wide">LIVE</span>
            </div>
          )}
          {lastSuccess && (
            <span className="text-xs text-gray-400">
              Last deploy: {formatRelativeTime(lastSuccess.startedAt)}
            </span>
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════ */}
      {/* PIPELINE TAB                                           */}
      {/* ═══════════════════════════════════════════════════════ */}
      {activeTab === 'pipeline' && (
        <div className="space-y-6">
          {/* ── What You're About To Do (Guided) ── */}
          {guidedMode && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-5 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-600/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <button
                    onClick={() => setShowExplainer(!showExplainer)}
                    className="flex items-center gap-2 w-full text-left"
                  >
                    <h3 className="text-sm font-bold text-gray-900">What You're About To Do</h3>
                    <svg
                      className={`w-4 h-4 text-gray-400 transition-transform ${showExplainer ? '' : '-rotate-90'}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                    >
                      <path d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                    </svg>
                  </button>
                  {showExplainer && (
                    <div className="mt-3 space-y-3">
                      <div>
                        <p className="text-sm font-semibold text-gray-800">What is a deployment?</p>
                        <p className="text-[13px] text-gray-600 mt-1">
                          A deployment takes the latest code changes and publishes them to the live
                          website at <strong>ldaja.ai</strong>. Think of it like updating an app on
                          your phone &mdash; the new version replaces the old one.
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800">What happens during deployment?</p>
                        <ol className="text-[13px] text-gray-600 mt-1 space-y-1 list-decimal list-inside">
                          <li>The code is <strong>compiled</strong> (converted from developer code into optimized files the browser can run)</li>
                          <li>Everything is <strong>packaged</strong> into a single zip file</li>
                          <li>That package is <strong>uploaded</strong> to Microsoft Azure (our cloud hosting provider)</li>
                          <li>The system <strong>verifies</strong> the site is working correctly</li>
                        </ol>
                      </div>
                      <div className="flex gap-6">
                        <div>
                          <p className="text-sm font-semibold text-gray-800">How long does it take?</p>
                          <p className="text-[13px] text-gray-600 mt-0.5">Typically 3-5 minutes from start to finish.</p>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-800">Is there downtime?</p>
                          <p className="text-[13px] text-gray-600 mt-0.5">Brief (10-30 seconds) while Azure swaps in the new version.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── Status Cards ── */}
          <div className="grid grid-cols-4 gap-4">
            {/* Pipeline Status */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Pipeline Status</span>
                <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                </svg>
              </div>
              <Badge variant={statusBadge.variant} dot>{statusBadge.label}</Badge>
              <p className="text-[11px] text-gray-400 mt-2">
                {state.status === 'idle' ? 'Ready for new build' : state.error || 'Pipeline active'}
              </p>
              {guidedMode && (
                <p className="text-[10px] text-blue-500/70 mt-1 italic">The current stage of the deployment process</p>
              )}
            </div>

            {/* Last Successful Deploy */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Last Success</span>
                <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {lastSuccess ? formatRelativeTime(lastSuccess.startedAt) : '--'}
              </div>
              <p className="text-[11px] text-gray-400 mt-1">
                {lastSuccess
                  ? new Date(lastSuccess.startedAt).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })
                  : 'No successful deploys yet'}
              </p>
              {guidedMode && (
                <p className="text-[10px] text-blue-500/70 mt-1 italic">The most recent time we successfully updated the live site</p>
              )}
            </div>

            {/* Files in Package */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Package Files</span>
                <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {lastSuccess?.filesChanged?.toLocaleString() ?? '--'}
              </div>
              <p className="text-[11px] text-gray-400 mt-1">deploy.zip</p>
              {guidedMode && (
                <p className="text-[10px] text-blue-500/70 mt-1 italic">How many files are included in the update package</p>
              )}
            </div>

            {/* Deploy Count Today */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Deploys Today</span>
                <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M3 4.5h14.25M3 9h9.75M3 13.5h9.75m4.5-4.5v12m0 0l-3.75-3.75M17.25 21l3.75-3.75" />
                </svg>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-gray-900">{todayDeploys.length}</span>
                {todayDeploys.length > 0 && (
                  <span className={`text-sm font-medium ${todaySuccessRate === 100 ? 'text-green-600' : 'text-yellow-600'}`}>
                    {todaySuccessRate}% success
                  </span>
                )}
              </div>
              <p className="text-[11px] text-gray-400 mt-1">
                {todayDeploys.length > 0
                  ? `${todayDeploys.filter((d) => d.status === 'success').length} of ${todayDeploys.length} healthy`
                  : 'No deploys today'}
              </p>
              {guidedMode && (
                <p className="text-[10px] text-blue-500/70 mt-1 italic">How many times we've pushed updates today</p>
              )}
            </div>
          </div>

          {/* ── Pipeline Stages Visualization ── */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-sm font-semibold text-gray-900">Pipeline Stages</h3>
              <div className="flex items-center gap-3 text-[11px] text-gray-400">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-300" /> Waiting</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> Running</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /> Complete</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Failed</span>
              </div>
            </div>
            {guidedMode && (
              <p className="text-[13px] text-gray-500 mb-4 -mt-2">
                Each step below must complete successfully before the next one begins. Hover over any stage for more details.
              </p>
            )}

            <div className="flex items-start justify-between px-2">
              {PIPELINE_STAGES.map((stage, idx) => {
                const stageState = getStageState(idx, state.status);
                const isLast = idx === PIPELINE_STAGES.length - 1;

                // Stage box styles
                let boxClasses = 'w-14 h-14 rounded-xl border-2 flex items-center justify-center transition-all duration-500';
                let iconColor = 'text-gray-400';
                let stageContent: React.ReactNode = stage.icon;

                switch (stageState) {
                  case 'waiting':
                    boxClasses += ' border-gray-200 bg-gray-50';
                    break;
                  case 'active':
                    boxClasses += ' border-blue-500 bg-blue-50 animate-pulse';
                    iconColor = 'text-blue-600';
                    break;
                  case 'complete':
                    boxClasses += ' border-green-500 bg-green-50';
                    iconColor = 'text-green-600';
                    stageContent = (
                      <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                    );
                    break;
                  case 'failed':
                    boxClasses += ' border-red-500 bg-red-50';
                    iconColor = 'text-red-600';
                    stageContent = (
                      <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    );
                    break;
                }

                // Connector line
                let connectorColor = 'bg-gray-200';
                if (stageState === 'complete') connectorColor = 'bg-green-500';
                if (stageState === 'active') connectorColor = 'bg-blue-500';

                return (
                  <div key={stage.key} className={`flex items-start ${isLast ? '' : 'flex-1'}`}>
                    <div className="relative flex flex-col items-center group">
                      <div className={`${boxClasses} ${iconColor}`}>
                        {stageContent}
                      </div>
                      <span className="text-[11px] font-semibold text-gray-600 mt-2 whitespace-nowrap text-center">
                        {stage.label}
                      </span>
                      {guidedMode && (
                        <span className="text-[9px] text-gray-400 mt-0.5 text-center leading-tight max-w-[120px]">
                          {stage.description}
                        </span>
                      )}
                      <span className="text-[10px] text-gray-400 mt-0.5">
                        {stageState === 'active' && elapsedMs > 0 ? formatDuration(elapsedMs) : '--'}
                      </span>

                      {/* Tooltip */}
                      <div className="hidden group-hover:block absolute top-full mt-2 left-1/2 -translate-x-1/2 w-64 bg-gray-900 text-white rounded-lg p-3 shadow-xl z-50">
                        <p className="text-xs font-semibold mb-1">{stage.label}</p>
                        <p className="text-[11px] text-gray-300 leading-relaxed">{stage.tooltip}</p>
                        <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-gray-900 rotate-45" />
                      </div>
                    </div>
                    {!isLast && (
                      <div className={`flex-1 h-0.5 ${connectorColor} mx-2 mt-7 transition-all duration-500`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Action Buttons ── */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleStartBuild}
              disabled={!canStartBuild}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-sm shadow-blue-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
            >
              {isBuilding ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Building...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 010 1.972l-11.54 6.347a1.125 1.125 0 01-1.667-.986V5.653z" />
                  </svg>
                  Start Build
                </>
              )}
            </button>
            <button
              onClick={() => {
                setDeployChecks([false, false, false]);
                setShowDeployModal(true);
              }}
              disabled={!canDeploy}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z" />
              </svg>
              Deploy to Production
            </button>
            <button
              onClick={handleCancel}
              disabled={!canCancel}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-red-600 text-sm font-semibold rounded-lg border border-red-200 hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
              Cancel
            </button>

            {/* Environment Info */}
            <div className="ml-auto flex items-center gap-3">
              <span className="text-[11px] text-gray-400 flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
                </svg>
                app-ldaja-aidlc-web
              </span>
              <span className="text-[11px] text-gray-400 flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                </svg>
                West US 2
              </span>
              <span className="text-[11px] text-gray-400 flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z" />
                </svg>
                B1 Linux / Node 20 LTS
              </span>
            </div>
          </div>

          {/* ── Build Error ── */}
          {buildError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
                <p className="text-sm text-red-700">{buildError}</p>
                <button onClick={() => setBuildError(null)} className="ml-auto text-red-400 hover:text-red-600">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* ── What's Happening Now (Guided) ── */}
          {guidedMode && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-4 h-4 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
                </svg>
                <span className="text-xs font-bold text-indigo-800">What's Happening Now</span>
              </div>
              <p
                className="text-[13px] text-indigo-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: getPlainEnglishText(state.status) }}
              />
            </div>
          )}

          {/* ── Log Viewer + File Diff ── */}
          <div className="grid grid-cols-3 gap-4">
            {/* Log Viewer (2/3 width) */}
            <div className="col-span-2 bg-[#1e1e2e] rounded-xl border border-gray-700 shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 bg-[#181825] border-b border-gray-700">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-300">Build Output</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-700 text-gray-400">
                    {state.status === 'idle' ? 'Idle' : isBuilding || isDeploying ? 'Running' : 'Done'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={clearLogs}
                    className="text-[10px] text-gray-500 hover:text-gray-300 transition-colors"
                  >
                    Clear
                  </button>
                  <label className="flex items-center gap-1 text-[10px] text-gray-500">
                    <input
                      type="checkbox"
                      checked={autoScroll}
                      onChange={(e) => setAutoScroll(e.target.checked)}
                      className="w-3 h-3 rounded border-gray-600 bg-gray-700"
                    />
                    Auto-scroll
                  </label>
                </div>
              </div>
              <div
                ref={logViewerRef}
                className="h-80 overflow-y-auto p-4 font-mono text-[12px] leading-relaxed"
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#444 #1e1e2e',
                }}
              >
                {logs.length === 0 ? (
                  <div className="text-gray-600 italic">Waiting for build to start...</div>
                ) : (
                  logs.map((entry, i) => (
                    <LogLine key={i} entry={entry} />
                  ))
                )}
              </div>
            </div>

            {/* File Diff (1/3 width) */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <button
                onClick={() => setShowFileDiff(!showFileDiff)}
                className="w-full flex items-center justify-between px-4 py-2.5 border-b border-gray-100 hover:bg-gray-50 transition-colors"
              >
                <span className="text-xs font-semibold text-gray-900">What Changed Since Last Deploy</span>
                <div className="flex items-center gap-2">
                  <svg
                    className={`w-4 h-4 text-gray-400 transition-transform ${showFileDiff ? '' : '-rotate-90'}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                  >
                    <path d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                  </svg>
                </div>
              </button>
              {guidedMode && showFileDiff && (
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                  <p className="text-[11px] text-gray-500 leading-relaxed">
                    These are the files that differ between what's currently live on ldaja.ai and what we're about to deploy.{' '}
                    <span className="text-green-600 font-medium">Green = new files</span>,{' '}
                    <span className="text-yellow-600 font-medium">Yellow = modified</span>,{' '}
                    <span className="text-red-600 font-medium">Red = removed</span>.
                  </p>
                </div>
              )}
              {showFileDiff && (
                <div className="overflow-y-auto max-h-64">
                  <div className="text-center py-8 text-[11px] text-gray-400 italic">
                    File diff available after build completes
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Deployment History ── */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Deployment History</h3>
                {guidedMode && (
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    A record of every time we pushed an update to the live site.
                  </p>
                )}
              </div>
              <span className="text-[11px] text-gray-400">Last 7 days</span>
            </div>
            {historyLoading ? (
              <LoadingSpinner message="Loading history..." size="sm" />
            ) : history.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-400">No deployment history yet</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left">
                      <th className="px-5 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                      <th className="px-5 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Triggered By</th>
                      <th className="px-5 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Started</th>
                      <th className="px-5 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Duration</th>
                      <th className="px-5 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Files</th>
                      <th className="px-5 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Health Check</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {history.map((record) => (
                      <tr key={record.id} className={`hover:bg-gray-50 transition-colors ${record.status === 'failed' ? 'hover:bg-red-50/30' : ''}`}>
                        <td className="px-5 py-3">
                          <Badge variant={record.status === 'success' ? 'success' : 'danger'} dot>
                            {record.status === 'success' ? 'Success' : 'Failed'}
                          </Badge>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-blue-600/10 flex items-center justify-center">
                              <span className="text-[10px] font-bold text-blue-600">
                                {record.triggeredBy
                                  .split(' ')
                                  .map((w) => w[0])
                                  .join('')
                                  .toUpperCase()
                                  .slice(0, 2)}
                              </span>
                            </div>
                            <span className="text-[13px] text-gray-700">{record.triggeredBy}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-[13px] text-gray-600">
                          {new Date(record.startedAt).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </td>
                        <td className="px-5 py-3 text-[13px] text-gray-600 font-mono">
                          {formatDuration(record.duration)}
                        </td>
                        <td className="px-5 py-3 text-[13px] text-gray-600 font-mono">
                          {record.filesChanged?.toLocaleString() ?? '--'}
                        </td>
                        <td className="px-5 py-3">
                          {record.healthCheckStatus ? (
                            <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${record.healthCheckStatus === 200 ? 'text-green-600' : 'text-red-600'}`}>
                              {record.healthCheckStatus === 200 ? (
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              ) : (
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                </svg>
                              )}
                              {record.healthCheckStatus} {record.healthCheckStatus === 200 ? 'OK' : 'Error'}
                              {record.healthCheckTime ? ` (${record.healthCheckTime}ms)` : ''}
                            </span>
                          ) : record.status === 'failed' ? (
                            <span className="text-[11px] text-red-600 font-medium">
                              {record.failedStage ? `Failed at ${record.failedStage}` : 'Build failed'}
                            </span>
                          ) : (
                            <span className="text-[11px] text-gray-400">--</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════ */}
      {/* AZURE DASHBOARD TAB                                    */}
      {/* ═══════════════════════════════════════════════════════ */}
      {activeTab === 'dashboard' && (
        <AzureDashboardTab
          kpiData={kpiData}
          kpiLoading={kpiLoading}
          kpiError={kpiError}
          kpiIsLive={kpiIsLive}
          kpiLastUpdated={kpiLastUpdated}
          guidedMode={guidedMode}
          onRefresh={fetchKpis}
        />
      )}

      {/* ═══════════════════════════════════════════════════════ */}
      {/* DEPLOY CONFIRMATION MODAL                              */}
      {/* ═══════════════════════════════════════════════════════ */}
      {showDeployModal && (
        <div className="fixed inset-0 z-[100]">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowDeployModal(false)}
          />
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[560px] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 bg-amber-50 border-b border-amber-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-amber-900">Deploy to Production</h3>
                  <p className="text-xs text-amber-700">You are about to update the live production website at ldaja.ai</p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              {/* What will happen */}
              <div className="mb-4">
                <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">What Will Happen</div>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-100 space-y-1.5">
                  <p className="text-[13px] text-gray-700 flex items-start gap-2"><span className="text-amber-500 mt-0.5">1.</span> The new code package will be uploaded to Microsoft Azure</p>
                  <p className="text-[13px] text-gray-700 flex items-start gap-2"><span className="text-amber-500 mt-0.5">2.</span> Azure will replace the currently running application</p>
                  <p className="text-[13px] text-gray-700 flex items-start gap-2"><span className="text-amber-500 mt-0.5">3.</span> <strong>All users</strong> currently on the site will be affected</p>
                  <p className="text-[13px] text-gray-700 flex items-start gap-2"><span className="text-amber-500 mt-0.5">4.</span> The site may be briefly unavailable (<strong>10-30 seconds</strong>) during the swap</p>
                </div>
              </div>

              {/* Target */}
              <div className="mb-4">
                <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Target Environment</div>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">https://ldaja.ai</div>
                      <div className="text-[11px] text-gray-500 mt-0.5">app-ldaja-aidlc-web &bull; rg-ldaja-aidlc-prod &bull; West US 2</div>
                    </div>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold">PRODUCTION</span>
                  </div>
                </div>
              </div>

              {/* Pre-deploy checklist */}
              <div className="mb-4">
                <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Pre-Deploy Checklist</div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                  {[
                    'Build completed successfully (no errors)',
                    'Log output looks correct (no warnings I don\'t understand)',
                    'File changes are expected (reviewed the diff)',
                  ].map((text, idx) => (
                    <label key={idx} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={deployChecks[idx]}
                        onChange={(e) => {
                          const next = [...deployChecks];
                          next[idx] = e.target.checked;
                          setDeployChecks(next);
                        }}
                        className="w-4 h-4 rounded border-blue-300 text-blue-600"
                      />
                      <span className="text-[13px] text-blue-800">{text}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Reversibility note */}
              <div className="flex items-center gap-2 text-[11px] text-gray-500 mb-2">
                <svg className="w-3.5 h-3.5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                </svg>
                This action can be reversed using the <strong>Rollback</strong> button if something goes wrong.
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowDeployModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeploy}
                disabled={!allChecked}
                className="inline-flex items-center gap-2 px-5 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z" />
                </svg>
                {allChecked ? 'Deploy Now' : 'Complete Checklist to Deploy'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────

/** Single log line with color-coded level */
function LogLine({ entry }: { entry: LogEntry }) {
  const colorClass =
    entry.level === 'stderr'
      ? 'text-red-400'
      : entry.level === 'system'
        ? 'text-blue-400'
        : 'text-green-400';

  const timestamp = new Date(entry.timestamp).toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  return (
    <div className="flex gap-2 hover:bg-white/5 px-1 -mx-1 rounded">
      <span className="text-gray-600 flex-shrink-0 select-none">{timestamp}</span>
      <span className={`text-[10px] font-semibold uppercase w-12 flex-shrink-0 mt-px ${colorClass}`}>
        {entry.level === 'stderr' ? 'ERR' : entry.level === 'system' ? 'SYS' : 'OUT'}
      </span>
      <span className={colorClass} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
        {entry.message}
      </span>
    </div>
  );
}

// ── Azure Dashboard Tab Component ─────────────────────────────────

interface AzureDashboardTabProps {
  kpiData: AzureKpiData | null;
  kpiLoading: boolean;
  kpiError: string | null;
  kpiIsLive: boolean;
  kpiLastUpdated: Date | null;
  guidedMode: boolean;
  onRefresh: () => void;
}

function AzureDashboardTab({
  kpiData,
  kpiLoading,
  kpiError,
  kpiIsLive,
  kpiLastUpdated,
  guidedMode,
  onRefresh,
}: AzureDashboardTabProps) {
  const d = kpiData;
  const [toddDispatchStatus, setToddDispatchStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [toddDispatchMessage, setToddDispatchMessage] = useState('');

  // Evaluate all KPIs for issues (try-catch guards against malformed data)
  const issues: DashboardIssues | null = useMemo(() => {
    if (!d) return null;
    try {
      return evaluateAllKpis(d);
    } catch (err) {
      console.error('[Troubleshoot] KPI evaluation failed:', err);
      return null;
    }
  }, [d]);

  const issueCount = useMemo(() => {
    if (!issues) return { critical: 0, warning: 0, info: 0, total: 0 };
    return countIssues(issues);
  }, [issues]);

  // Dispatch Todd to investigate an issue
  const handleAskTodd = useCallback(async (issue: TroubleshootInfo) => {
    setToddDispatchStatus('sending');
    setToddDispatchMessage('');
    try {
      const prompt = generateToddPrompt(issue);
      await apiClient.post('/chat/conversations/conv-todd/execute', {
        toAgent: 'todd',
        subject: `Investigate: ${issue.title}`,
        prompt,
        priority: issue.severity === 'critical' ? 'high' : 'normal',
      });
      setToddDispatchStatus('sent');
      setToddDispatchMessage(`Todd has been dispatched to investigate: "${issue.title}". Check the Chat or Activity Feed for his findings.`);
    } catch {
      setToddDispatchStatus('error');
      setToddDispatchMessage('Could not dispatch Todd. Make sure the backend server is running and the dispatcher is active.');
    }
  }, []);

  return (
    <div className="space-y-6">
      {/* ── Guided Mode: What is this dashboard? ── */}
      {guidedMode && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-bold text-gray-900">What is this dashboard?</h3>
              <p className="text-[13px] text-gray-600 mt-2 leading-relaxed">
                This is a real-time view of the Azure cloud infrastructure that powers <strong>ldaja.ai</strong>.
                It shows whether services are up, how fast the site is running, how much we are spending,
                and if there are any security concerns. Think of it as the vital signs monitor for our website.
              </p>
              <div className="flex gap-6 mt-3">
                <div>
                  <p className="text-[12px] font-semibold text-gray-700">Who is this for?</p>
                  <p className="text-[12px] text-gray-500 mt-0.5">Anyone who wants to know if ldaja.ai is healthy and running well.</p>
                </div>
                <div>
                  <p className="text-[12px] font-semibold text-gray-700">How often does it update?</p>
                  <p className="text-[12px] text-gray-500 mt-0.5">Metrics refresh every 60 seconds from Azure Monitor.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Last Updated Bar ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-gray-900">Azure Infrastructure Overview</h3>
          <span className="text-[11px] text-gray-400">Subscription: aidlc-prod &bull; Region: West US 2</span>
          {kpiIsLive && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" />LIVE
            </span>
          )}
          {!kpiIsLive && kpiData && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-yellow-100 text-yellow-700">
              (mock data)
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-gray-400">
            Last refreshed:{' '}
            <span className="font-medium text-gray-600">
              {kpiLastUpdated
                ? kpiLastUpdated.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
                : '--'}
            </span>
          </span>
          {kpiLoading && (
            <span className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          )}
          <button
            onClick={onRefresh}
            disabled={kpiLoading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-40"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* ── Issue Summary Banner ── */}
      {issueCount.total > 0 && (
        <div className={`rounded-xl border p-4 shadow-sm ${
          issueCount.critical > 0
            ? 'bg-red-50 border-red-200'
            : issueCount.warning > 0
              ? 'bg-amber-50 border-amber-200'
              : 'bg-blue-50 border-blue-200'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
              issueCount.critical > 0 ? 'bg-red-100' : issueCount.warning > 0 ? 'bg-amber-100' : 'bg-blue-100'
            }`}>
              <svg className={`w-5 h-5 ${
                issueCount.critical > 0 ? 'text-red-600' : issueCount.warning > 0 ? 'text-amber-600' : 'text-blue-600'
              }`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>
            <div className="flex-1">
              <p className={`text-sm font-semibold ${
                issueCount.critical > 0 ? 'text-red-800' : issueCount.warning > 0 ? 'text-amber-800' : 'text-blue-800'
              }`}>
                {issueCount.total} Issue{issueCount.total > 1 ? 's' : ''} Detected
              </p>
              <p className={`text-[12px] mt-0.5 ${
                issueCount.critical > 0 ? 'text-red-600' : issueCount.warning > 0 ? 'text-amber-600' : 'text-blue-600'
              }`}>
                {[
                  issueCount.critical > 0 && `${issueCount.critical} critical`,
                  issueCount.warning > 0 && `${issueCount.warning} warning`,
                  issueCount.info > 0 && `${issueCount.info} informational`,
                ].filter(Boolean).join(', ')}
                {' '}&mdash; scroll down and click "What does this mean?" on any highlighted card for details and troubleshooting steps.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Todd Dispatch Status ── */}
      {toddDispatchStatus !== 'idle' && (
        <div className={`rounded-xl border p-4 ${
          toddDispatchStatus === 'sent'
            ? 'bg-green-50 border-green-200'
            : toddDispatchStatus === 'error'
              ? 'bg-red-50 border-red-200'
              : 'bg-blue-50 border-blue-200'
        }`}>
          <div className="flex items-center gap-3">
            {toddDispatchStatus === 'sending' && (
              <span className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
            )}
            {toddDispatchStatus === 'sent' && (
              <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            {toddDispatchStatus === 'error' && (
              <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
            )}
            <p className={`text-[12px] ${
              toddDispatchStatus === 'sent' ? 'text-green-700' : toddDispatchStatus === 'error' ? 'text-red-700' : 'text-blue-700'
            }`}>
              {toddDispatchStatus === 'sending' ? 'Dispatching Todd to investigate...' : toddDispatchMessage}
            </p>
            {toddDispatchStatus !== 'sending' && (
              <button
                onClick={() => setToddDispatchStatus('idle')}
                aria-label="Dismiss"
                className="ml-auto text-gray-400 hover:text-gray-600"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Error Banner ── */}
      {kpiError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-800">Backend Unreachable</p>
              <p className="text-xs text-red-600 mt-0.5">{kpiError}</p>
            </div>
            <button onClick={onRefresh} className="text-xs font-semibold text-red-700 bg-red-100 px-3 py-1 rounded-lg hover:bg-red-200 transition-colors">
              Retry
            </button>
          </div>
        </div>
      )}

      {!d ? (
        <LoadingSpinner message="Loading Azure metrics..." />
      ) : (
        <>
          {/* ═══ ROW 1: SERVICE HEALTH ═══ */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Service Health</h4>
              {guidedMode && <span className="text-[10px] text-blue-500/70 italic">Are all our services running? Green = healthy, Red = down</span>}
            </div>
            <div className="grid grid-cols-5 gap-4">
              {/* App Service */}
              <KpiCard
                title="App Service"
                dot={d.serviceHealth.available && d.serviceHealth.state === 'Running'}
                dotPulse
                badgeOk={d.serviceHealth.state === 'Running'}
                badgeLabel={d.serviceHealth.state || 'Unknown'}
                detail={d.serviceHealth.defaultHostName || 'app-ldaja-aidlc-web'}
                detailMono
                guidedText={guidedMode ? 'The main web server hosting ldaja.ai' : undefined}
                troubleshoot={issues?.appService}
                onAskTodd={handleAskTodd}
                toddSending={toddDispatchStatus === 'sending'}
              />
              {/* DNS */}
              <KpiCard
                title="DNS"
                dot={d.dnsStatus.available && (d.dnsStatus.allHealthy ?? false)}
                badgeOk={d.dnsStatus.allHealthy ?? false}
                badgeLabel={d.dnsStatus.allHealthy ? 'Resolving' : 'Issues'}
                detail={`ldaja.ai -> ${d.dnsStatus.okCount ?? 0}/${d.dnsStatus.totalCount ?? 4} NS OK`}
                detailMono
                guidedText={guidedMode ? 'Can users find our website address?' : undefined}
                troubleshoot={issues?.dns}
                onAskTodd={handleAskTodd}
                toddSending={toddDispatchStatus === 'sending'}
              />
              {/* SSL */}
              <KpiCard
                title="SSL Certificate"
                dot={d.sslStatus.available && (d.sslStatus.isValid ?? false)}
                badgeOk={d.sslStatus.isValid ?? false}
                badgeLabel={d.sslStatus.isValid ? 'Valid' : 'Invalid'}
                badgeIcon="lock"
                detail={`${d.sslStatus.issuer || 'Unknown'} - ${d.sslStatus.daysUntilExpiry ?? '?'} days left`}
                guidedText={guidedMode ? 'The security lock icon in your browser' : undefined}
                troubleshoot={issues?.ssl}
                onAskTodd={handleAskTodd}
                toddSending={toddDispatchStatus === 'sending'}
              />
              {/* Health Check */}
              <KpiCard
                title="Health Check"
                dot={d.healthCheck.available && d.healthCheck.statusCode === 200}
                badgeOk={d.healthCheck.statusCode === 200}
                badgeLabel={d.healthCheck.statusCode === 200 ? '200 OK' : `${d.healthCheck.statusCode || 'N/A'}`}
                detail={`/api/health - ${d.healthCheck.responseTime ?? '?'}ms`}
                guidedText={guidedMode ? 'Is the server responding to requests?' : undefined}
                troubleshoot={issues?.healthCheck}
                onAskTodd={handleAskTodd}
                toddSending={toddDispatchStatus === 'sending'}
              />
              {/* Azure Region */}
              <KpiCard
                title="Azure Region"
                dot
                badgeOk
                badgeLabel="No Outages"
                detail="West US 2 - All services normal"
                guidedText={guidedMode ? "Is Microsoft's data center working?" : undefined}
              />
            </div>
          </div>

          {/* ═══ ROW 2: PERFORMANCE METRICS ═══ */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Performance Metrics</h4>
              {guidedMode && <span className="text-[10px] text-blue-500/70 italic">How hard is the server working and how fast is it responding?</span>}
            </div>
            <div className="grid grid-cols-4 gap-4">
              {/* CPU */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">CPU Usage</span>
                </div>
                <div className="text-2xl font-bold text-gray-900 mb-2">
                  {d.performanceMetrics.cpu?.current ?? '--'}%
                </div>
                <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-600 ${barColor(d.performanceMetrics.cpu?.current ?? 0)}`}
                    style={{ width: `${d.performanceMetrics.cpu?.current ?? 0}%` }}
                  />
                </div>
                <p className="text-[10px] text-gray-400 mt-2">B1 tier - 1 core / 1.75 GB</p>
                {guidedMode && <p className="text-[10px] text-blue-500/70 mt-1 italic">How much processing power the server is using (lower is better)</p>}
                {issues?.cpu && (
                  <InlineTroubleshoot issue={issues.cpu} onAskTodd={handleAskTodd} toddSending={toddDispatchStatus === 'sending'} />
                )}
              </div>

              {/* Memory */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Memory Usage</span>
                </div>
                <div className="text-2xl font-bold text-gray-900 mb-2">
                  {d.performanceMetrics.memory?.current ?? '--'}%
                </div>
                <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-600 ${barColor(d.performanceMetrics.memory?.current ?? 0)}`}
                    style={{ width: `${d.performanceMetrics.memory?.current ?? 0}%` }}
                  />
                </div>
                <p className="text-[10px] text-gray-400 mt-2">
                  {d.performanceMetrics.memory?.workingSetMB
                    ? `${(d.performanceMetrics.memory.workingSetMB / 1024).toFixed(2)} GB / 1.75 GB used`
                    : '-- / 1.75 GB'}
                </p>
                {guidedMode && <p className="text-[10px] text-blue-500/70 mt-1 italic">How much RAM the server is using (watch if this goes above 80%)</p>}
                {issues?.memory && (
                  <InlineTroubleshoot issue={issues.memory} onAskTodd={handleAskTodd} toddSending={toddDispatchStatus === 'sending'} />
                )}
              </div>

              {/* Response Time */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Avg Response Time</span>
                </div>
                <div className="text-2xl font-bold text-gray-900 mb-2">
                  {d.performanceMetrics.responseTime ?? '--'}ms
                </div>
                <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-600 ${barColor(((d.performanceMetrics.responseTime ?? 0) / 1000) * 100)}`}
                    style={{ width: `${Math.min(((d.performanceMetrics.responseTime ?? 0) / 1000) * 100, 100)}%` }}
                  />
                </div>
                <p className="text-[10px] text-gray-400 mt-2">Target: &lt; 500ms</p>
                {guidedMode && <p className="text-[10px] text-blue-500/70 mt-1 italic">How quickly the server answers requests (lower = faster)</p>}
                {issues?.responseTime && (
                  <InlineTroubleshoot issue={issues.responseTime} onAskTodd={handleAskTodd} toddSending={toddDispatchStatus === 'sending'} />
                )}
              </div>

              {/* Requests */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Requests (Last Hour)</span>
                </div>
                <div className="text-2xl font-bold text-gray-900 mb-3">
                  {(d.performanceMetrics.requests ?? 0).toLocaleString()}
                </div>
                <div className="flex gap-3">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-medium">
                    2xx: {(d.performanceMetrics.http2xx ?? 0).toLocaleString()}
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700 font-medium">
                    4xx: {(d.performanceMetrics.http4xx ?? 0).toLocaleString()}
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium">
                    5xx: {(d.performanceMetrics.http5xx ?? 0).toLocaleString()}
                  </span>
                </div>
                {guidedMode && <p className="text-[10px] text-blue-500/70 mt-2 italic">How many page loads and API calls happened in the last hour</p>}
                {issues?.serverErrors && (
                  <InlineTroubleshoot issue={issues.serverErrors} onAskTodd={handleAskTodd} toddSending={toddDispatchStatus === 'sending'} />
                )}
              </div>
            </div>
          </div>

          {/* ═══ ROW 3: SECURITY & DEPLOYMENT HEALTH ═══ */}
          <div>
            <div className="grid grid-cols-2 gap-6">
              {/* Security & Identity */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Security &amp; Identity (Entra ID)</h4>
                  {guidedMode && <span className="text-[10px] text-blue-500/70 italic">Login activity and authentication health</span>}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <KpiCard title="Failed Logins (24h)" valueText="2" detail="Last: Feb 11, 3:14 AM (wrong password)" dot guidedText={guidedMode ? 'How many unsuccessful login attempts' : undefined} />
                  <KpiCard title="Active Sessions" valueText="3" detail="Dan (CEO), Todd (Admin), Marcus (Dev)" guidedText={guidedMode ? 'Users currently logged into the portal' : undefined} />
                  <KpiCard title="App Registration" dot badgeOk badgeLabel="Healthy" detail="b2e0812d-9bb6-44ab-..." detailMono guidedText={guidedMode ? 'The Entra ID app that handles user sign-in' : undefined} />
                  <KpiCard title="Managed Identity" dot badgeOk badgeLabel="Active" detail="Token refresh: OK (last 4m ago)" guidedText={guidedMode ? 'Lets the app securely talk to other Azure services' : undefined} />
                </div>
              </div>

              {/* Deployment Health */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Deployment Health</h4>
                  {guidedMode && <span className="text-[10px] text-blue-500/70 italic">Status of the most recent code deployment</span>}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <KpiCard title="Last Deploy" dot badgeOk badgeLabel="Success" detail="Most recent deployment" guidedText={guidedMode ? 'The most recent time code was pushed live' : undefined} />
                  <KpiCard title="Running Version" valueText="v2.0.0" valueMono detail="Commit: e4ac990" guidedText={guidedMode ? 'Which version of the code is currently live' : undefined} />
                  <KpiCard title="Startup Time" valueText="32s" detail="Cold start - node server/dist/index.js" guidedText={guidedMode ? 'How long the app takes to boot after a deploy' : undefined} />
                  <KpiCard title="HTTPS Redirect" dot badgeOk badgeLabel="Enforced" detail="HTTP -> HTTPS auto-redirect active" guidedText={guidedMode ? 'All traffic is forced to use secure HTTPS' : undefined} />
                </div>
              </div>
            </div>
          </div>

          {/* ═══ ROW 4: COST & BUDGET ═══ */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Cost &amp; Budget</h4>
              {guidedMode && <span className="text-[10px] text-blue-500/70 italic">How much are we spending on Azure cloud hosting this month?</span>}
            </div>
            <div className="grid grid-cols-4 gap-4">
              {/* Current Spend */}
              <div className="col-span-2 bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
                    {new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' })} Spend vs Budget
                  </span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                    (d.costData.projectedMonthEnd ?? 0) <= 100
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {(d.costData.projectedMonthEnd ?? 0) <= 100 ? 'On Track' : 'Over Budget'}
                  </span>
                </div>
                <div className="flex items-baseline gap-3 mb-3">
                  <span className="text-3xl font-bold text-gray-900">${(d.costData.totalCost ?? 0).toFixed(2)}</span>
                  <span className="text-sm text-gray-400">of $100.00 budget</span>
                </div>
                <div className="h-3 rounded-full bg-gray-100 overflow-hidden mb-2">
                  <div
                    className="h-full rounded-full bg-blue-600 transition-all duration-600"
                    style={{ width: `${Math.min((d.costData.totalCost ?? 0), 100)}%` }}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-gray-400">
                    {((d.costData.totalCost ?? 0)).toFixed(1)}% of monthly budget used
                  </span>
                  <span className="text-[10px] text-gray-400">
                    Day {d.costData.dayOfMonth ?? '?'} of {d.costData.daysInMonth ?? '?'}
                  </span>
                </div>
                {guidedMode && <p className="text-[10px] text-blue-500/70 mt-2 italic">Total money spent so far this month on all Azure services. Budget is $100/month.</p>}
              </div>

              {/* Daily Burn Rate */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Daily Burn Rate</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-gray-900">${(d.costData.dailyBurnRate ?? 0).toFixed(2)}</span>
                  <span className="text-xs text-gray-400">/day</span>
                </div>
                <p className="text-[10px] text-gray-400 mt-2">Avg over last {d.costData.dayOfMonth ?? '?'} days</p>
                {guidedMode && <p className="text-[10px] text-blue-500/70 mt-1 italic">How much we spend per day on average</p>}
              </div>

              {/* Projected Month End */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Projected Month-End</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                    (d.costData.projectedMonthEnd ?? 0) <= 100
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {(d.costData.projectedMonthEnd ?? 0) <= 100 ? 'Under Budget' : 'Over Budget'}
                  </span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className={`text-2xl font-bold ${(d.costData.projectedMonthEnd ?? 0) <= 100 ? 'text-green-600' : 'text-red-600'}`}>
                    ${(d.costData.projectedMonthEnd ?? 0).toFixed(2)}
                  </span>
                </div>
                <p className="text-[10px] text-gray-400 mt-2">
                  ${(100 - (d.costData.projectedMonthEnd ?? 0)).toFixed(2)} projected headroom
                </p>
                {guidedMode && <p className="text-[10px] text-blue-500/70 mt-1 italic">Estimated total spend by end of month</p>}
                {issues?.cost && (
                  <InlineTroubleshoot issue={issues.cost} onAskTodd={handleAskTodd} toddSending={toddDispatchStatus === 'sending'} />
                )}
              </div>
            </div>

            {/* Cost Breakdown */}
            {d.costData.costByService && (
              <div className="mt-4 bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Cost by Resource (MTD)</span>
                  {guidedMode && <span className="text-[10px] text-blue-500/70 italic">Which services cost the most money</span>}
                </div>
                <div className="space-y-3">
                  {Object.entries(d.costData.costByService)
                    .sort(([, a], [, b]) => b - a)
                    .map(([service, cost]) => {
                      const total = d.costData.totalCost || 1;
                      const pct = (cost / total) * 100;
                      const colors = ['bg-blue-600', 'bg-purple-500', 'bg-cyan-500', 'bg-amber-500', 'bg-green-500'];
                      const colorIdx = Object.keys(d.costData.costByService!).indexOf(service);
                      return (
                        <div key={service} className="flex items-center gap-3">
                          <span className="text-[11px] text-gray-600 w-40 truncate">{service}</span>
                          <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                            <div
                              className={`h-full rounded-full ${colors[colorIdx % colors.length]}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="text-[11px] font-semibold text-gray-700 w-16 text-right">${cost.toFixed(2)}</span>
                          <span className="text-[10px] text-gray-400 w-10 text-right">{pct.toFixed(1)}%</span>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>

          {/* ═══ ROW 5: ALERTS ═══ */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Alerts &amp; Incidents</h4>
              {guidedMode && <span className="text-[10px] text-blue-500/70 italic">Automated warnings when something needs attention</span>}
            </div>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <KpiCard
                title="Active Alerts"
                valueText={issues?.alerts ? String((d.recentAlerts.alerts?.filter(a => a.status === 'Active') ?? []).length) : '0'}
                valueColor={issues?.alerts ? 'text-red-600' : 'text-green-600'}
                dot={!issues?.alerts}
                detail={issues?.alerts ? 'Unresolved alerts detected' : 'No unresolved alerts'}
                guidedText={guidedMode ? 'Problems that need immediate attention' : undefined}
                troubleshoot={issues?.alerts}
                onAskTodd={handleAskTodd}
                toddSending={toddDispatchStatus === 'sending'}
              />
              <KpiCard title="Alert Rules" valueText="6" detail="Configured & monitoring" guidedText={guidedMode ? 'Automatic monitors watching for problems' : undefined} />
              <KpiCard title="Alerts This Week" valueText={String(d.recentAlerts.totalCount ?? 0)} detail="All resolved" guidedText={guidedMode ? 'Total alerts that fired in the last 7 days' : undefined} />
            </div>

            {/* Alert History Table */}
            {d.recentAlerts.alerts && d.recentAlerts.alerts.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
                  <span className="text-xs font-semibold text-gray-900">Recent Alert History</span>
                  <span className="text-[11px] text-gray-400">Last 7 days</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left">
                        <th className="px-5 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Severity</th>
                        <th className="px-5 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Alert Name</th>
                        <th className="px-5 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Triggered</th>
                        <th className="px-5 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Duration</th>
                        <th className="px-5 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {d.recentAlerts.alerts.map((alert, idx) => {
                        const sevColors: Record<string, string> = {
                          Critical: 'bg-red-100 text-red-700',
                          Warning: 'bg-yellow-100 text-yellow-700',
                          Info: 'bg-blue-100 text-blue-700',
                        };
                        const statusColors: Record<string, string> = {
                          Resolved: 'bg-green-100 text-green-700',
                          Informational: 'bg-gray-100 text-gray-600',
                          Active: 'bg-red-100 text-red-700',
                        };
                        return (
                          <tr key={idx} className="hover:bg-gray-50 transition-colors">
                            <td className="px-5 py-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${sevColors[alert.severity] || 'bg-gray-100 text-gray-600'}`}>
                                {alert.severity}
                              </span>
                            </td>
                            <td className="px-5 py-3 text-[13px] text-gray-700">{alert.name}</td>
                            <td className="px-5 py-3 text-[13px] text-gray-600">{alert.triggered}</td>
                            <td className="px-5 py-3 text-[13px] text-gray-600 font-mono">{alert.duration}</td>
                            <td className="px-5 py-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusColors[alert.status] || 'bg-gray-100 text-gray-600'}`}>
                                {alert.status}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* ═══ ROW 6: DNS & NETWORKING ═══ */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">DNS &amp; Networking</h4>
              {guidedMode && <span className="text-[10px] text-blue-500/70 italic">Are the internet plumbing and domain settings working correctly?</span>}
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div className="grid grid-cols-4 gap-6">
                {/* Nameservers */}
                <div>
                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide block mb-2">Azure DNS Nameservers</span>
                  <div className="space-y-1.5">
                    {(d.dnsStatus.nameservers || []).map((ns) => (
                      <div key={ns.nameserver} className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${ns.status === 'ok' ? 'bg-green-500' : 'bg-red-500'}`} />
                        <span className="text-[11px] text-gray-600 font-mono">{ns.nameserver}</span>
                      </div>
                    ))}
                  </div>
                  {guidedMode && (
                    <p className="text-[10px] text-blue-500/70 mt-2 italic">
                      {d.dnsStatus.okCount}/{d.dnsStatus.totalCount} nameservers responding = {d.dnsStatus.allHealthy ? 'fully propagated' : 'issues detected'}
                    </p>
                  )}
                </div>

                {/* Custom Domain */}
                <div>
                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide block mb-2">Custom Domain Binding</span>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500" />
                      <span className="text-[11px] text-gray-600">ldaja.ai &rarr; bound</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500" />
                      <span className="text-[11px] text-gray-600">www.ldaja.ai &rarr; redirect</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500" />
                      <span className="text-[11px] text-gray-600">TXT verification: valid</span>
                    </div>
                  </div>
                  {guidedMode && <p className="text-[10px] text-blue-500/70 mt-2 italic">The domain name is properly linked to our server</p>}
                </div>

                {/* CORS */}
                <div>
                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide block mb-2">CORS Configuration</span>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500" />
                      <span className="text-[11px] text-gray-600">https://ldaja.ai</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500" />
                      <span className="text-[11px] text-gray-600">http://localhost:3000</span>
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-2">2 allowed origins configured</p>
                  {guidedMode && <p className="text-[10px] text-blue-500/70 mt-1 italic">Which websites are allowed to talk to our API</p>}
                </div>

                {/* Application Insights */}
                <div>
                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide block mb-2">Application Insights</span>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500" />
                      <span className="text-[11px] text-gray-600">Connected &amp; receiving data</span>
                    </div>
                    <p className="text-[10px] text-gray-400 font-mono truncate">Key: 145a9fd1-a61e-...</p>
                    <p className="text-[10px] text-gray-400">Last telemetry: 12s ago</p>
                  </div>
                  {guidedMode && <p className="text-[10px] text-blue-500/70 mt-2 italic">Monitoring tool that tracks errors and performance</p>}
                </div>
              </div>
            </div>
          </div>

          {/* ── Footer ── */}
          <div className="text-center py-4">
            <p className="text-[11px] text-gray-400">
              Dashboard designed by <strong>Todd Harrison</strong> (Network Administrator) &bull; Data sourced from Azure Monitor, App Insights &amp; Entra ID
            </p>
            <p className="text-[10px] text-gray-300 mt-1">
              Last full audit: Feb 11, 2026 &bull; Next scheduled: Feb 18, 2026
            </p>
          </div>
        </>
      )}
    </div>
  );
}

// ── Inline Troubleshoot Expander (for non-KpiCard metrics) ────────

function InlineTroubleshoot({
  issue,
  onAskTodd,
  toddSending,
}: {
  issue: TroubleshootInfo;
  onAskTodd: (issue: TroubleshootInfo) => void;
  toddSending?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  const sevColor =
    issue.severity === 'critical'
      ? 'text-red-600 hover:text-red-700'
      : issue.severity === 'warning'
        ? 'text-amber-600 hover:text-amber-700'
        : 'text-blue-600 hover:text-blue-700';

  return (
    <div className="mt-2 pt-2 border-t border-gray-100">
      <button
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        className={`flex items-center gap-1.5 text-[11px] font-semibold transition-colors ${sevColor}`}
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
        </svg>
        {expanded ? 'Hide details' : 'What does this mean?'}
        <svg
          className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </button>
      {expanded && (
        <TroubleshootPanel issue={issue} onAskTodd={onAskTodd} toddSending={toddSending} />
      )}
    </div>
  );
}

// ── Reusable KPI Card ─────────────────────────────────────────────

interface KpiCardProps {
  title: string;
  /** Show a status dot */
  dot?: boolean;
  /** Whether the dot should pulse (animate) */
  dotPulse?: boolean;
  /** Show a status badge instead of a value */
  badgeOk?: boolean;
  badgeLabel?: string;
  badgeIcon?: 'check' | 'lock';
  /** Show a large text value instead of badge */
  valueText?: string;
  valueMono?: boolean;
  valueColor?: string;
  /** Detail line below the main value */
  detail?: string;
  detailMono?: boolean;
  /** Guided mode helper text */
  guidedText?: string;
  /** Troubleshooting info when issue detected */
  troubleshoot?: TroubleshootInfo | null;
  /** Callback when "Ask Todd" is clicked */
  onAskTodd?: (issue: TroubleshootInfo) => void;
  /** Whether Todd dispatch is currently in-flight */
  toddSending?: boolean;
}

function KpiCard({
  title,
  dot,
  dotPulse,
  badgeOk,
  badgeLabel,
  badgeIcon,
  valueText,
  valueMono,
  valueColor,
  detail,
  detailMono,
  guidedText,
  troubleshoot,
  onAskTodd,
  toddSending,
}: KpiCardProps) {
  const [expanded, setExpanded] = useState(false);
  const hasIssue = troubleshoot && troubleshoot.severity !== 'healthy';

  const borderColor = hasIssue
    ? troubleshoot.severity === 'critical'
      ? 'border-red-300'
      : troubleshoot.severity === 'warning'
        ? 'border-amber-300'
        : 'border-blue-200'
    : 'border-gray-200';

  return (
    <div className={`bg-white rounded-xl border ${borderColor} p-4 shadow-sm hover:shadow-md transition-shadow`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{title}</span>
        <div className="flex items-center gap-1.5">
          {hasIssue && (
            <span
              className={`w-2 h-2 rounded-full ${
                troubleshoot.severity === 'critical' ? 'bg-red-500 animate-pulse' : troubleshoot.severity === 'warning' ? 'bg-amber-500' : 'bg-blue-400'
              }`}
              title={troubleshoot.title}
            />
          )}
          {dot !== undefined && (
            dotPulse ? (
              <span className="relative flex h-3 w-3">
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${dot ? 'bg-green-400' : 'bg-red-400'}`} />
                <span className={`relative inline-flex rounded-full h-3 w-3 ${dot ? 'bg-green-500' : 'bg-red-500'}`} />
              </span>
            ) : (
              <span className={`w-3 h-3 rounded-full ${dot ? 'bg-green-500' : 'bg-red-500'}`} />
            )
          )}
        </div>
      </div>

      {badgeLabel !== undefined && (
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-bold ${
            badgeOk ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {badgeIcon === 'lock' ? (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
              </svg>
            ) : (
              badgeOk ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              )
            )}
            {badgeLabel}
          </span>
        </div>
      )}

      {valueText !== undefined && !badgeLabel && (
        <div className={`text-2xl font-bold ${valueColor || 'text-gray-900'} ${valueMono ? 'font-mono' : ''}`}>
          {valueText}
        </div>
      )}

      {detail && (
        <p className={`text-[10px] text-gray-400 mt-2 ${detailMono ? 'font-mono' : ''}`}>{detail}</p>
      )}
      {guidedText && (
        <p className="text-[10px] text-blue-500/70 mt-1 italic">{guidedText}</p>
      )}

      {/* Troubleshooting expander */}
      {hasIssue && (
        <div className="mt-3 pt-2 border-t border-gray-100">
          <button
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
            className={`flex items-center gap-1.5 text-[11px] font-semibold transition-colors ${
              troubleshoot.severity === 'critical'
                ? 'text-red-600 hover:text-red-700'
                : troubleshoot.severity === 'warning'
                  ? 'text-amber-600 hover:text-amber-700'
                  : 'text-blue-600 hover:text-blue-700'
            }`}
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
            </svg>
            {expanded ? 'Hide details' : 'What does this mean?'}
            <svg
              className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
              <path d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
          </button>

          {expanded && (
            <TroubleshootPanel issue={troubleshoot} onAskTodd={onAskTodd} toddSending={toddSending} />
          )}
        </div>
      )}
    </div>
  );
}

// ── Troubleshoot Panel ────────────────────────────────────────────

function TroubleshootPanel({
  issue,
  onAskTodd,
  toddSending,
}: {
  issue: TroubleshootInfo;
  onAskTodd?: (issue: TroubleshootInfo) => void;
  toddSending?: boolean;
}) {
  const sevBg =
    issue.severity === 'critical'
      ? 'bg-red-50 border-red-200'
      : issue.severity === 'warning'
        ? 'bg-amber-50 border-amber-200'
        : 'bg-blue-50 border-blue-200';

  const sevHeaderText =
    issue.severity === 'critical'
      ? 'text-red-800'
      : issue.severity === 'warning'
        ? 'text-amber-800'
        : 'text-blue-800';

  const sevBodyText =
    issue.severity === 'critical'
      ? 'text-red-700'
      : issue.severity === 'warning'
        ? 'text-amber-700'
        : 'text-blue-700';

  const sevBadge =
    issue.severity === 'critical'
      ? 'bg-red-100 text-red-700'
      : issue.severity === 'warning'
        ? 'bg-amber-100 text-amber-700'
        : 'bg-blue-100 text-blue-700';

  return (
    <div className={`mt-3 rounded-lg border ${sevBg} p-3 space-y-3`}>
      {/* Severity + Title */}
      <div className="flex items-start gap-2">
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide ${sevBadge}`}>
          {issue.severity}
        </span>
        <p className={`text-[12px] font-semibold ${sevHeaderText} leading-tight`}>{issue.title}</p>
      </div>

      {/* Explanation */}
      <p className={`text-[11px] ${sevBodyText} leading-relaxed`}>{issue.explanation}</p>

      {/* Possible Causes */}
      <div>
        <p className={`text-[10px] font-semibold ${sevHeaderText} uppercase tracking-wide mb-1`}>Possible Causes</p>
        <ul className="space-y-1">
          {issue.possibleCauses.map((cause, i) => (
            <li key={i} className={`text-[11px] ${sevBodyText} flex items-start gap-1.5`}>
              <span className="mt-1 flex-shrink-0 w-1 h-1 rounded-full bg-current opacity-50" />
              {cause}
            </li>
          ))}
        </ul>
      </div>

      {/* Troubleshooting Steps */}
      <div>
        <p className={`text-[10px] font-semibold ${sevHeaderText} uppercase tracking-wide mb-1.5`}>Troubleshooting Steps</p>
        <div className="space-y-2">
          {issue.steps.map((step, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${sevBadge}`}>
                {i + 1}
              </span>
              <div className="flex-1 min-w-0">
                <p className={`text-[11px] font-semibold ${sevHeaderText}`}>
                  {step.action}
                  {step.type === 'portal' && (
                    <span className="ml-1.5 text-[9px] font-normal px-1 py-0.5 rounded bg-purple-100 text-purple-600">Portal</span>
                  )}
                  {step.type === 'cli' && (
                    <span className="ml-1.5 text-[9px] font-normal px-1 py-0.5 rounded bg-gray-200 text-gray-600">CLI</span>
                  )}
                </p>
                <p className={`text-[10px] ${sevBodyText} mt-0.5 leading-relaxed`}>{step.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CLI Command */}
      {issue.cliCommand && (
        <div>
          <p className={`text-[10px] font-semibold ${sevHeaderText} uppercase tracking-wide mb-1`}>Quick CLI Check</p>
          <code className="block text-[10px] bg-gray-900 text-green-400 rounded px-2.5 py-1.5 font-mono overflow-x-auto">
            {issue.cliCommand}
          </code>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center gap-2 pt-1">
        <a
          href={issue.portalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-semibold text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200 transition-colors"
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
          </svg>
          Open in Azure Portal
        </a>
        {onAskTodd && (
          <button
            onClick={() => onAskTodd(issue)}
            disabled={toddSending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-semibold text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
            </svg>
            Ask Todd to Investigate
          </button>
        )}
      </div>
    </div>
  );
}
